// The value a saved-filter condition row stores, given the field the user chose (FilterForm's filter2)
// and what they picked or typed for it (filter4).
//
// FilterForm used to work this out twice. add() looked a picked option up by the field's dbNameField
// when it had one; edit() never did, so editing a condition on a List field whose dbNameField differs
// from its dbField left the row with no value - Enquiries Branch (dbField mbpBranchCode, dbNameField
// branchCode) on TEST 2026-09-14 - and saving would store a blank condition. Both now call this.
//
//   field     the db_view_fields row (fieldType, dbField, dbNameField, filterAllowStartWith)
//   picked    a dropdown option object, or the typed value
//   tempDate  the typed date when a Date field's modifier is "custom"
//   filter4Type  the input type FilterForm chose for a typed value
export function conditionValueFor(field, picked, { tempDate, filter4Type } = {}) {
  if (picked instanceof Object) {
    if (field.fieldType === 'Date') {
      return picked.dateModifierFunc === 'custom' ? tempDate : picked.dateModifierFunc
    }
    if (field.fieldType === 'Yes/No') {
      return picked.parentFlag
    }
    return picked[optionLabelKey(field)]
  }

  if (
    ['String', 'Number', 'Currency', 'Time'].includes(filter4Type) ||
    (field.filterAllowStartWith && typeof picked === 'string')
  ) {
    return picked
  }

  return undefined
}

// The option key a List field's condition value is read from: its dbNameField when it has one, else the last
// segment of dbField. A saved condition stores that label, and the picked option is found again by it, both when
// the condition is edited (FilterForm.editCondition) and when the filter is saved (filterSaveRequest, which sends
// that option's dbIdField as dbValue). editCondition used to look by dbField only, so a condition on a field
// with a dbNameField reopened with no value picked.
//
// The activity lists' Quote/Ver fields (db_view_fields 357, 1557, 1587, 1622) have dbField versionNo, so with no
// dbNameField the picker showed and matched bare version numbers: 59,491 rows of "1", "2"..., and the first quote
// with the picked number was saved (TEST 01/11: Q51242-1, id 85608, saved as id 96193). Script 49 gives them
// dbNameField quoteVer ("Q51242-1"), which v1/activequotations returns.
export function optionLabelKey(field) {
  if (field.dbNameField) {
    return field.dbNameField
  }
  const segments = (field.dbField || '').split('.')
  return segments[segments.length - 1]
}

// The option a saved condition's value refers to. Labels are not always unique (21 quote/version labels are shared
// by two quotations on TEST); the first match wins, as it always has.
export function findConditionOption(field, options, value) {
  const key = optionLabelKey(field)
  return (options || []).find(option => option && option[key] === value)
}

// JEW-2: the id of the option picked for a List condition (its dbIdField), carried on the condition so
// the save sends the account the user picked, not the first one with the same name ("JEWSON LTD" x 442).
export function pickedDbValue(field, picked) {
  if (!(picked instanceof Object) || !field || !field.dbIdField) return undefined
  if (field.fieldType === 'Date' || field.fieldType === 'Yes/No') return undefined
  const id = picked[field.dbIdField]
  return id === null ? undefined : id
}

// JEW-2: the dbValue to save for a List condition. The id it carries (picked, or stored on a saved
// condition) wins; the name lookup (first match) is used only when there is no id, or the id belongs to
// an option with a different name (the value was changed some other way).
export function resolveDbValue(field, options, condition) {
  const key = optionLabelKey(field)
  const id = condition && condition.dbValue
  if (id !== null && id !== undefined && id !== '') {
    const byId = (options || []).find(o => o && String(o[field.dbIdField]) === String(id))
    if (!byId || byId[key] === condition.value) return id
  }
  const byName = findConditionOption(field, options, condition && condition.value)
  return byName ? byName[field.dbIdField] : undefined
}

// JEW-3: the option with a saved condition's stored id, only when that option still has the
// condition's name; undefined otherwise (no id, unknown id, or the name was changed another way).
export function findOptionByStoredId(field, options, condition) {
  const id = condition && condition.dbValue
  if (id === null || id === undefined || id === '' || !field || !field.dbIdField) return undefined
  const key = optionLabelKey(field)
  const byId = (options || []).find(o => o && String(o[field.dbIdField]) === String(id))
  return byId && byId[key] === condition.value ? byId : undefined
}

// JEW-2: the option a saved condition is reopened on for edit: the one with its stored id when that
// option still has the condition's name, else the first with the name (as findConditionOption).
export function findConditionOptionById(field, options, condition) {
  return findOptionByStoredId(field, options, condition) ||
    findConditionOption(field, options, condition && condition.value)
}
